import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Icon,
  InputLabel,
  MenuItem,
  PhoneInput,
  Stepper,
  Select,
  Stack,
  Text,
  TextField,
  Typography,
} from '@fse/ui';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { farmersControllerCreate } from '../api/generated/farmers/farmers';
import { FseApiError } from '../api/client';

/**
 * Add-Farmer wizard.
 *
 * Five steps. Address is intentionally a "coming soon" placeholder
 * (visible-but-disabled) until the FICA Tier-2 schema work lands; same
 * pattern for "Add GPS later" on the Location step. Both surface the
 * planned shape without doing the migration today.
 *
 * Submission is a single POST /farmers — the backend writes users +
 * farmer_profiles + audit_log inside one transaction so partial
 * registrations can't happen.
 */

const PREF_LANGS = [
  { value: 'en', label: 'English' },
  { value: 'ts', label: 'Xitsonga' },
  { value: 'nso', label: 'Sepedi' },
  { value: 'xi', label: 'isiXhosa' },
] as const;

const PROVINCES = [
  'Limpopo',
  'Gauteng',
  'Mpumalanga',
  'KwaZulu-Natal',
  'Eastern Cape',
  'Western Cape',
  'Northern Cape',
  'Free State',
  'North West',
];

interface IdentityState {
  phone: string;
  displayName: string;
  preferredLang: 'en' | 'xi' | 'nso' | 'ts';
}
interface LocationState {
  lat: string;
  lng: string;
  gpsLater: boolean; // disabled in v1 — preserved for the future schema change
}
interface FarmState {
  treeCount: string;
  estCapacityKg: string;
}

/**
 * Phone validation mirrors the server-side Zod schema in
 * services/api/src/farmers/dto.ts — libphonenumber-js with country
 * context 'ZA'. We normalise to E.164 client-side too so the user sees
 * "+27821234567" after entering "0821234567" or "+27 82 123 4567".
 *
 * Schemas are built per-render from t() so their validation messages follow the
 * chosen language (TD-072). Memoised on t so the object identity is stable
 * (mirrors signup.tsx).
 */
function buildSchemas(t: TFunction) {
  const identitySchema = z.object({
    phone: z
      .string()
      .min(1, t('addFarmer.validation.phoneRequired'))
      .transform((raw, ctx) => {
        const parsed = parsePhoneNumberFromString(raw, 'ZA');
        if (!parsed?.isValid()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('addFarmer.validation.phoneInvalid'),
          });
          return z.NEVER;
        }
        return parsed.number;
      }),
    displayName: z
      .string()
      .min(1, t('addFarmer.validation.nameRequired'))
      .max(200, t('addFarmer.validation.nameTooLong')),
    preferredLang: z.enum(['en', 'xi', 'nso', 'ts']),
  });

  const locationSchema = z.object({
    lat: z
      .string()
      .regex(/^-?\d+(\.\d+)?$/, t('addFarmer.validation.enterNumber'))
      .transform(Number)
      .pipe(z.number().min(-90).max(90, t('addFarmer.validation.latRange'))),
    lng: z
      .string()
      .regex(/^-?\d+(\.\d+)?$/, t('addFarmer.validation.enterNumber'))
      .transform(Number)
      .pipe(z.number().min(-180).max(180, t('addFarmer.validation.lngRange'))),
  });

  const farmSchema = z.object({
    treeCount: z
      .string()
      .regex(/^\d+$/, t('addFarmer.validation.wholeNumber'))
      .transform(Number)
      .pipe(z.number().int().min(0)),
    estCapacityKg: z.union([
      z.literal(''),
      z
        .string()
        .regex(/^\d+$/, t('addFarmer.validation.wholeNumber'))
        .transform(Number)
        .pipe(z.number().int().min(0)),
    ]),
  });

  return { identitySchema, locationSchema, farmSchema };
}

// Number of wizard steps — logic-only constant (the visible step labels are
// resolved per-render through t() so they follow the active language).
const STEP_COUNT = 5;

export function NewFarmerScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Wizard step labels (resolved per-render so they follow the language).
  const STEPS = [
    t('addFarmer.step.identity'),
    t('addFarmer.step.location'),
    t('addFarmer.step.address'),
    t('addFarmer.step.farmDetails'),
    t('addFarmer.step.review'),
  ];

  const { identitySchema, locationSchema, farmSchema } = useMemo(() => buildSchemas(t), [t]);
  const [activeStep, setActiveStep] = useState(0);
  const [identity, setIdentity] = useState<IdentityState>({
    phone: '',
    displayName: '',
    preferredLang: 'en',
  });
  const [location, setLocation] = useState<LocationState>({
    lat: '',
    lng: '',
    gpsLater: false,
  });
  const [farm, setFarm] = useState<FarmState>({ treeCount: '', estCapacityKg: '' });

  // Per-step field errors. Cleared on every successful "Next".
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: () => {
      // identity.phone was already normalised by the Zod transform on
      // Next-from-Identity. location.{lat,lng} are stringly-typed in
      // form state; we numeric-ify here. farmGps is null when the user
      // checks "Add GPS later".
      const payload = {
        phone: identity.phone,
        displayName: identity.displayName,
        preferredLang: identity.preferredLang,
        farmGps: location.gpsLater
          ? null
          : { lat: Number(location.lat), lng: Number(location.lng) },
        treeCount: Number(farm.treeCount),
        estCapacityKg: farm.estCapacityKg === '' ? null : Number(farm.estCapacityKg),
      };
      return farmersControllerCreate(payload);
    },
    onSuccess: () => {
      navigate({ to: '/farmers' });
    },
  });

  function validateCurrent(): boolean {
    setErrors({});
    if (activeStep === 0) {
      const r = identitySchema.safeParse(identity);
      if (!r.success) {
        setErrors(zodToFieldErrors(r.error));
        return false;
      }
      // Persist the normalised phone back into state so the Review step
      // and the eventual POST see the E.164 form.
      setIdentity({ ...identity, phone: r.data.phone });
    } else if (activeStep === 1) {
      // Location step — when "Add GPS later" is checked, lat/lng aren't
      // required. Otherwise validate as before.
      if (!location.gpsLater) {
        const r = locationSchema.safeParse(location);
        if (!r.success) {
          setErrors(zodToFieldErrors(r.error));
          return false;
        }
      }
    } else if (activeStep === 2) {
      // Address step — nothing to validate, all fields are disabled.
    } else if (activeStep === 3) {
      const r = farmSchema.safeParse(farm);
      if (!r.success) {
        setErrors(zodToFieldErrors(r.error));
        return false;
      }
    }
    return true;
  }

  function next(): void {
    if (validateCurrent()) {
      setActiveStep((s) => Math.min(s + 1, STEP_COUNT - 1));
    }
  }
  function back(): void {
    setActiveStep((s) => Math.max(s - 1, 0));
  }

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Text variant="headline-medium" as="h2">
          {t('addFarmer.title')}
        </Text>
        <Button variant="text" onClick={() => navigate({ to: '/farmers' })}>
          {t('addFarmer.cancel')}
        </Button>
      </Box>

      <Stepper steps={STEPS} activeStep={activeStep} />

      <Card>
        <CardContent>
          {activeStep === 0 && (
            <IdentityStep state={identity} setState={setIdentity} errors={errors} />
          )}
          {activeStep === 1 && (
            <LocationStep state={location} setState={setLocation} errors={errors} />
          )}
          {activeStep === 2 && <AddressStep />}
          {activeStep === 3 && (
            <FarmDetailsStep state={farm} setState={setFarm} errors={errors} />
          )}
          {activeStep === 4 && (
            <ReviewStep identity={identity} location={location} farm={farm} />
          )}
        </CardContent>
      </Card>

      {createMutation.isError && (
        <Alert severity="error">
          {createMutation.error instanceof FseApiError && createMutation.error.status === 409
            ? t('addFarmer.error.conflict', { phone: identity.phone })
            : t('addFarmer.error.generic', { error: String(createMutation.error) })}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          disabled={activeStep === 0}
          onClick={back}
          startIcon={<Icon name="arrow_back" size={20} />}
        >
          {t('addFarmer.back')}
        </Button>
        {activeStep < STEP_COUNT - 1 ? (
          <Button variant="contained" onClick={next} endIcon={<Icon name="arrow_forward" size={20} />}>
            {t('addFarmer.next')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            startIcon={
              createMutation.isPending ? <CircularProgress size={16} /> : <Icon name="check" size={20} />
            }
          >
            {createMutation.isPending ? t('addFarmer.registering') : t('addFarmer.register')}
          </Button>
        )}
      </Box>
    </Stack>
  );
}

function IdentityStep({
  state,
  setState,
  errors,
}: {
  state: IdentityState;
  setState: (s: IdentityState) => void;
  errors: Record<string, string>;
}) {
  // The wizard stores `state.phone` as the source of truth (local digits while
  // typing, then E.164 after the Identity step validates — written back by
  // validateCurrent). PhoneInput works in 9-digit local form, so we surface the
  // trailing 9 digits of whatever is stored and write raw local digits back on
  // change. ZA-context parsing in identitySchema accepts both forms, so
  // validation + the E.164 payload are unchanged.
  const { t } = useTranslation();
  const localPhone = state.phone.replace(/\D/g, '').slice(-9);
  return (
    <Stack spacing={2}>
      <CardHeader title={t('addFarmer.identity.title')} subheader={t('addFarmer.identity.subtitle')} sx={{ pl: 0, pt: 0 }} />
      <PhoneInput
        value={localPhone}
        onChangeText={(cleaned) => setState({ ...state, phone: cleaned })}
        error={errors.phone}
        hint={t('addFarmer.identity.phoneHint')}
      />
      <TextField
        label={t('addFarmer.identity.displayName')}
        value={state.displayName}
        onChange={(e) => setState({ ...state, displayName: e.target.value })}
        error={!!errors.displayName}
        helperText={errors.displayName ?? t('addFarmer.identity.displayNameHint')}
        fullWidth
      />
      <FormControl fullWidth>
        <InputLabel id="lang-label">{t('addFarmer.identity.preferredLang')}</InputLabel>
        <Select
          labelId="lang-label"
          label={t('addFarmer.identity.preferredLang')}
          value={state.preferredLang}
          onChange={(e) =>
            setState({ ...state, preferredLang: e.target.value as IdentityState['preferredLang'] })
          }
        >
          {PREF_LANGS.map((l) => (
            <MenuItem key={l.value} value={l.value}>
              {l.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {t('addFarmer.identity.preferredLangHint')}
        </FormHelperText>
      </FormControl>
    </Stack>
  );
}

function LocationStep({
  state,
  setState,
  errors,
}: {
  state: LocationState;
  setState: (s: LocationState) => void;
  errors: Record<string, string>;
}) {
  const { t } = useTranslation();
  const [geolocStatus, setGeolocStatus] = useState<
    'idle' | 'pending' | 'denied' | 'unavailable' | 'timeout' | 'success'
  >('idle');

  function useMyLocation(): void {
    if (!navigator.geolocation) {
      setGeolocStatus('unavailable');
      return;
    }
    setGeolocStatus('pending');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setState({
          ...state,
          lat: pos.coords.latitude.toFixed(6),
          lng: pos.coords.longitude.toFixed(6),
        });
        setGeolocStatus('success');
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setGeolocStatus('denied');
        else if (err.code === err.TIMEOUT) setGeolocStatus('timeout');
        else setGeolocStatus('unavailable');
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 },
    );
  }

  return (
    <Stack spacing={2}>
      <CardHeader
        title={t('addFarmer.location.title')}
        subheader={t('addFarmer.location.subtitle')}
        sx={{ pl: 0, pt: 0 }}
      />

      <Button
        variant="outlined"
        onClick={useMyLocation}
        disabled={geolocStatus === 'pending' || state.gpsLater}
        startIcon={
          geolocStatus === 'pending' ? (
            <CircularProgress size={16} />
          ) : (
            <Icon name="my_location" size={20} />
          )
        }
      >
        {geolocStatus === 'pending' ? t('addFarmer.location.reading') : t('addFarmer.location.useMyLocation')}
      </Button>

      {!state.gpsLater && geolocStatus === 'denied' && (
        <Alert severity="info">
          {t('addFarmer.location.denied')}
        </Alert>
      )}
      {!state.gpsLater && geolocStatus === 'unavailable' && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={useMyLocation}>
              {t('addFarmer.location.retryGps')}
            </Button>
          }
        >
          {t('addFarmer.location.unavailable')}
        </Alert>
      )}
      {!state.gpsLater && geolocStatus === 'timeout' && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={useMyLocation}>
              {t('addFarmer.location.retryGps')}
            </Button>
          }
        >
          {t('addFarmer.location.timeout')}
        </Alert>
      )}
      {!state.gpsLater && geolocStatus === 'success' && (
        <Alert severity="success">{t('addFarmer.location.captured')}</Alert>
      )}

      <Stack direction="row" spacing={2}>
        <TextField
          label={t('addFarmer.location.latitude')}
          placeholder="-23.83"
          value={state.lat}
          onChange={(e) => setState({ ...state, lat: e.target.value })}
          error={!!errors.lat}
          helperText={errors.lat ?? t('addFarmer.location.latHint')}
          disabled={state.gpsLater}
          fullWidth
        />
        <TextField
          label={t('addFarmer.location.longitude')}
          placeholder="30.16"
          value={state.lng}
          onChange={(e) => setState({ ...state, lng: e.target.value })}
          error={!!errors.lng}
          helperText={errors.lng ?? t('addFarmer.location.lngHint')}
          disabled={state.gpsLater}
          fullWidth
        />
      </Stack>

      <FormControlLabel
        control={
          <Checkbox
            checked={state.gpsLater}
            onChange={(e) => setState({ ...state, gpsLater: e.target.checked })}
          />
        }
        label={
          <Typography variant="body2">
            {t('addFarmer.location.gpsLater')} <code>—</code> {t('addFarmer.location.gpsLaterSuffix')}
          </Typography>
        }
      />
    </Stack>
  );
}

function AddressStep() {
  const { t } = useTranslation();
  return (
    <Stack spacing={2}>
      <CardHeader
        title={t('addFarmer.address.title')}
        subheader={t('addFarmer.address.subtitle')}
        sx={{ pl: 0, pt: 0 }}
      />
      <Alert severity="info">
        {t('addFarmer.address.notice')}
      </Alert>
      <TextField label={t('addFarmer.address.line1')} disabled placeholder={t('addFarmer.address.line1Placeholder')} fullWidth />
      <TextField label={t('addFarmer.address.line2')} disabled placeholder={t('addFarmer.address.line2Placeholder')} fullWidth />
      <Stack direction="row" spacing={2}>
        <TextField label={t('addFarmer.address.suburb')} disabled fullWidth />
        <TextField label={t('addFarmer.address.city')} disabled fullWidth />
      </Stack>
      <Stack direction="row" spacing={2}>
        <TextField label={t('addFarmer.address.postalCode')} disabled placeholder="0000" fullWidth />
        <FormControl fullWidth disabled>
          <InputLabel id="province-label">{t('addFarmer.address.province')}</InputLabel>
          <Select labelId="province-label" label={t('addFarmer.address.province')} value="">
            {PROVINCES.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
}

function FarmDetailsStep({
  state,
  setState,
  errors,
}: {
  state: FarmState;
  setState: (s: FarmState) => void;
  errors: Record<string, string>;
}) {
  const { t } = useTranslation();
  return (
    <Stack spacing={2}>
      <CardHeader
        title={t('addFarmer.farm.title')}
        subheader={t('addFarmer.farm.subtitle')}
        sx={{ pl: 0, pt: 0 }}
      />
      <TextField
        label={t('addFarmer.farm.treeCount')}
        type="text"
        value={state.treeCount}
        onChange={(e) => setState({ ...state, treeCount: e.target.value })}
        error={!!errors.treeCount}
        helperText={errors.treeCount ?? t('addFarmer.farm.treeCountHint')}
        fullWidth
      />
      <TextField
        label={t('addFarmer.farm.estCapacity')}
        type="text"
        value={state.estCapacityKg}
        onChange={(e) => setState({ ...state, estCapacityKg: e.target.value })}
        error={!!errors.estCapacityKg}
        helperText={errors.estCapacityKg ?? t('addFarmer.farm.estCapacityHint')}
        fullWidth
      />
    </Stack>
  );
}

function ReviewStep({
  identity,
  location,
  farm,
}: {
  identity: IdentityState;
  location: LocationState;
  farm: FarmState;
}) {
  const { t } = useTranslation();
  return (
    <Stack spacing={2}>
      <CardHeader
        title={t('addFarmer.review.title')}
        subheader={t('addFarmer.review.subtitle')}
        sx={{ pl: 0, pt: 0 }}
      />

      <ReviewRow label={t('addFarmer.review.phone')} value={identity.phone} />
      <ReviewRow label={t('addFarmer.review.name')} value={identity.displayName} />
      <ReviewRow
        label={t('addFarmer.review.preferredLang')}
        value={PREF_LANGS.find((l) => l.value === identity.preferredLang)?.label ?? identity.preferredLang}
      />
      <Divider />
      {location.gpsLater ? (
        <ReviewRow label={t('addFarmer.review.location')} value={t('addFarmer.review.locationLater')} muted />
      ) : (
        <>
          <ReviewRow label={t('addFarmer.review.latitude')} value={location.lat} />
          <ReviewRow label={t('addFarmer.review.longitude')} value={location.lng} />
        </>
      )}
      <ReviewRow label={t('addFarmer.review.address')} value={t('addFarmer.review.addressComingSoon')} muted />
      <Divider />
      <ReviewRow label={t('addFarmer.review.treeCount')} value={farm.treeCount} />
      <ReviewRow
        label={t('addFarmer.review.estCapacity')}
        value={farm.estCapacityKg === '' ? t('addFarmer.review.capacityUnknown') : t('addFarmer.review.capacityValue', { value: farm.estCapacityKg })}
      />
    </Stack>
  );
}

function ReviewRow({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" color={muted ? 'text.secondary' : 'text.primary'}>
        {value}
      </Typography>
    </Box>
  );
}

function zodToFieldErrors(error: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path[0];
    if (typeof key === 'string' && !out[key]) {
      out[key] = issue.message;
    }
  }
  return out;
}
